// log.service.ts

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Log channel type — use any string you like, e.g., 'synch', 'error', 'network' */
export type LogType = string;

@Injectable({ providedIn: 'root' })
export class LogService {
    /** Default maximum number of entries to keep per channel */
    private readonly defaultMax = 200;

    /** Per-channel streams */
    private channels = new Map<LogType, BehaviorSubject<string[]>>();

    /** Optional per-channel max sizes */
    private channelMax = new Map<LogType, number>();

    /**
     * Append a new message to the given channel (prepended with a timestamp, newest first).
     * Creates the channel lazily if it doesn't exist yet.
     */
    OBSOLETE__append(type: LogType, message: string): void {
        const subject = this.ensureChannel(type);
        const max = this.channelMax.get(type) ?? this.defaultMax;
        const timestamp = new Date().toLocaleTimeString();
        const current = subject.getValue();
        const updated = [`[${timestamp}] ${message}`, ...current].slice(0, max);
        subject.next(updated);
    }

append(type: LogType, ...parts: unknown[]): void {
  const subject = this.ensureChannel(type);
  const max = this.channelMax.get(type) ?? this.defaultMax;
  const timestamp = new Date().toLocaleTimeString();

  const text = parts.map(p => this.formatPart(p)).join(' ');
  const current = subject.getValue();

  // ⬇️ append to bottom (newest last)
  const updated = [...current, `[${timestamp}] ${text}`];
  console.log(`[${timestamp}] ${text}`);   
  // keep only the last `max` entries (drop oldest from the front)
  subject.next(updated.length > max ? updated.slice(updated.length - max) : updated);
}

    formatPart(v: unknown): string {
        if (v instanceof Error) return `${v.name}: ${v.message}`;
        if (typeof v === 'object' && v !== null) {
            try { return JSON.stringify(v); } catch { return String(v); }
        }
        return String(v);
    }


    /** Append multiple lines in one go (keeps order: first item becomes newest). */
    appendMany(type: LogType, messages: string[]): void {
        if (!messages?.length) return;
        // Add from last to first so the first item ends up on top (newest)
        for (let i = messages.length - 1; i >= 0; i--) {
            this.append(type, messages[i]);
        }
    }

    /** Clear a specific channel. */
    clear(type: LogType): void {
        const subject = this.ensureChannel(type);
        subject.next([]);
    }

    /** Clear all channels. */
    clearAll(): void {
        for (const subject of this.channels.values()) {
            subject.next([]);
        }
    }

    /** Set maximum number of entries to keep for a channel (affects future appends). */
    setMax(type: LogType, max: number): void {
        if (max <= 0) return;
        this.channelMax.set(type, max);
        // Enforce immediately on existing data
        const subject = this.ensureChannel(type);
        subject.next(subject.getValue().slice(0, max));
    }

    /** Get an observable stream of messages for a channel (newest first). */
    entries$(type: LogType): Observable<string[]> {
        return this.ensureChannel(type).asObservable();
    }

    /** Get the current messages value for a channel (imperative). */
    getValue(type: LogType): string[] {
        return this.ensureChannel(type).getValue();
    }

    /** List currently known channel names. */
    listChannels(): LogType[] {
        return Array.from(this.channels.keys());
    }

    // -- Internals --

    private ensureChannel(type: LogType): BehaviorSubject<string[]> {
        let subject = this.channels.get(type);
        if (!subject) {
            subject = new BehaviorSubject<string[]>([]);
            this.channels.set(type, subject);
        }
        return subject;
    }
}